/**
 * Hook that wires the pure logic in `needsMeNotifications.ts` into the
 * React lifecycle: tracks needs-me task IDs across renders, fires
 * browser Notifications when new ones appear, exposes an unseen count
 * for the bell pip and an `acknowledge()` action.
 *
 * Side effects gated on:
 *   - User has granted Notification permission AND
 *   - Tab is not currently visible (no point notifying what they can see)
 *   - First render is a "bootstrap" — the existing backlog is silently
 *     marked as notified so the user doesn't get spammed on page load.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { TaskItem } from '@/api/types';
import type { NeedsMeContext } from '@/lib/needsMe';
import {
  computeNeedsMeIds,
  formatNotificationBody,
  newSinceLastSeen,
  notificationsStorage,
  unseenCount as unseenCountFn,
  updateNotifiedIds,
  acknowledgeAll,
} from '@/lib/needsMeNotifications';

export type NotificationPermission = 'default' | 'granted' | 'denied' | 'unsupported';

interface UseNeedsMeNotificationsResult {
  /** Number of needs-me tasks the user hasn't acknowledged. Drives the bell pip. */
  unseenCount: number;
  /** Current Notification API permission state. */
  permission: NotificationPermission;
  /** Request browser Notification permission. Resolves with the new state. */
  requestPermission: () => Promise<NotificationPermission>;
  /** Mark every current needs-me task as acknowledged (e.g. on bell click). */
  acknowledge: () => void;
}

function readPermission(): NotificationPermission {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') {
    return 'unsupported';
  }
  return Notification.permission as NotificationPermission;
}

export function useNeedsMeNotifications(
  tasks: ReadonlyArray<TaskItem>,
  ctx: NeedsMeContext,
): UseNeedsMeNotificationsResult {
  // Identity ref so we re-bootstrap if the viewer changes.
  const ctxKeyRef = useRef<string>(`${ctx.meHandle ?? ''}|${ctx.meIsOwner ? '1' : '0'}`);
  const ctxKey = `${ctx.meHandle ?? ''}|${ctx.meIsOwner ? '1' : '0'}`;
  const [permission, setPermission] = useState<NotificationPermission>(() => readPermission());
  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<string>>(() =>
    notificationsStorage.loadAcknowledged(),
  );

  const currentNeedsMeIds = useMemo(() => computeNeedsMeIds(tasks, ctx), [tasks, ctx]);

  // Notification side-effect: fire on transition into needs-me.
  useEffect(() => {
    // Reset bootstrap if viewer identity changed.
    if (ctxKeyRef.current !== ctxKey) {
      ctxKeyRef.current = ctxKey;
    }

    const isBootstrapped = notificationsStorage.isBootstrapped();
    const notified = notificationsStorage.loadNotified();
    const newIds = newSinceLastSeen(currentNeedsMeIds, notified);

    if (!isBootstrapped) {
      // First-ever render: silently mark existing backlog as notified.
      // Don't spam the user with the entire current state.
      const seeded = updateNotifiedIds(currentNeedsMeIds, notified, [...currentNeedsMeIds]);
      notificationsStorage.saveNotified(seeded);
      notificationsStorage.markBootstrapped();
      return;
    }

    if (newIds.length === 0) {
      // Still prune `notified` of resolved tasks so they can re-fire later.
      const pruned = updateNotifiedIds(currentNeedsMeIds, notified, []);
      if (pruned.size !== notified.size) {
        notificationsStorage.saveNotified(pruned);
      }
      return;
    }

    // Fire system Notifications for each new needs-me task — but only when
    // the tab is hidden. If the user is looking at the board, the bell pip
    // is enough; system notifications would be obnoxious.
    const tabHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const canNotify = permission === 'granted' && tabHidden;

    if (canNotify) {
      const byId = new Map(tasks.map((t) => [t.id, t]));
      for (const id of newIds) {
        const task = byId.get(id);
        if (!task) continue;
        try {
          const { title, body } = formatNotificationBody(task);
          new Notification(title, {
            body,
            tag: `needs-me-${id}`, // dedupe: re-firing for the same id replaces
            requireInteraction: false,
          });
        } catch {
          // Notification constructor can throw on some platforms; degrade silently.
        }
      }
    }

    // Always update `notifiedIds`, even when we couldn't fire — being
    // permission-denied or foregrounded is an "already seen" equivalent.
    const updated = updateNotifiedIds(currentNeedsMeIds, notified, newIds);
    notificationsStorage.saveNotified(updated);
  }, [currentNeedsMeIds, ctxKey, tasks, permission]);

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (typeof Notification === 'undefined') {
      setPermission('unsupported');
      return 'unsupported';
    }
    try {
      const result = await Notification.requestPermission();
      setPermission(result as NotificationPermission);
      return result as NotificationPermission;
    } catch {
      setPermission('denied');
      return 'denied';
    }
  }, []);

  const acknowledge = useCallback(() => {
    const next = acknowledgeAll(currentNeedsMeIds);
    setAcknowledgedIds(next);
    notificationsStorage.saveAcknowledged(next);
  }, [currentNeedsMeIds]);

  const unseen = useMemo(
    () => unseenCountFn(currentNeedsMeIds, acknowledgedIds),
    [currentNeedsMeIds, acknowledgedIds],
  );

  return {
    unseenCount: unseen,
    permission,
    requestPermission,
    acknowledge,
  };
}
